import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import { useUIStore } from '@/stores/ui-store';

/**
 * Monthly budget for one platform-owned provider — a shared upstream account
 * (e.g. the ElevenLabs plan every site's voiceovers run on). Over the budget,
 * or over one site's share of it, the provider steps aside until next month
 * and the others serve. See services/platform/provider-budgets.ts.
 */

interface BudgetSummary {
  monthly_cap_usd: number | null;
  per_tenant_share: number | null;
  note: string | null;
  period: string;
  month_usd: number;
  month_calls: number;
  top_tenants: Array<{ tenant_site_id: string; usd: number; calls: number }>;
}

const usd = (v: number) => `$${v.toFixed(v < 1 ? 4 : 2)}`;

export default function ProviderBudgetPanel({ providerId }: { providerId: string }) {
  const addToast = useUIStore((s) => s.addToast);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [cap, setCap] = useState('');
  const [share, setShare] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<{ budget: BudgetSummary }>(`/admin/platform/ai/providers/${providerId}/budget`)
      .then((r) => {
        if (!live) return;
        setBudget(r.budget);
        setCap(r.budget.monthly_cap_usd == null ? '' : String(r.budget.monthly_cap_usd));
        setShare(r.budget.per_tenant_share == null ? '' : String(Math.round(r.budget.per_tenant_share * 100)));
      })
      .catch(() => live && setError('Could not load the budget'));
    return () => {
      live = false;
    };
  }, [providerId]);

  async function save() {
    const capNum = cap.trim() === '' ? null : Number(cap);
    const shareNum = share.trim() === '' ? null : Number(share) / 100;
    if (capNum != null && (!Number.isFinite(capNum) || capNum < 0)) return setError('The monthly cap must be a dollar amount');
    if (shareNum != null && (!Number.isFinite(shareNum) || shareNum <= 0 || shareNum > 1)) return setError('The per-site share must be between 1 and 100%');
    if (shareNum != null && capNum == null) return setError('A per-site share needs a monthly cap');
    setError(null);
    setSaving(true);
    try {
      const r = await api.put<{ budget: BudgetSummary }>(`/admin/platform/ai/providers/${providerId}/budget`, {
        monthly_cap_usd: capNum,
        per_tenant_share: shareNum,
      });
      setBudget(r.budget);
      addToast(capNum == null ? 'Budget removed' : 'Budget saved', 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the budget');
    } finally {
      setSaving(false);
    }
  }

  const capNow = budget?.monthly_cap_usd ?? null;
  const pct = capNow ? Math.min(100, Math.round((budget!.month_usd / capNow) * 100)) : null;

  return (
    <div className="mb-3 rounded-md border border-line bg-surface p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-ink">Monthly budget (this shared account, every site)</p>
        {budget && (
          <p className="text-xs text-ink-muted">
            {budget.period.slice(0, 7)}: {usd(budget.month_usd)}
            {capNow != null && ` of ${usd(capNow)} (${pct}%)`} · {budget.month_calls} call{budget.month_calls === 1 ? '' : 's'}
          </p>
        )}
      </div>
      {capNow != null && pct != null && (
        <div className="mt-1.5 h-1.5 w-full rounded-full bg-surface-muted" aria-label="Budget used">
          <div className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-sage-500'}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="text-xs text-ink-soft">
          Monthly cap (USD)
          <input
            type="number"
            min="0"
            step="0.5"
            value={cap}
            onChange={(e) => setCap(e.target.value)}
            placeholder="No cap"
            className="mt-0.5 block w-28 rounded border border-line bg-surface px-2 py-1 text-sm text-ink"
          />
        </label>
        <label className="text-xs text-ink-soft">
          Max per site (%)
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            value={share}
            onChange={(e) => setShare(e.target.value)}
            placeholder="Any"
            className="mt-0.5 block w-24 rounded border border-line bg-surface px-2 py-1 text-sm text-ink"
          />
        </label>
        <Button size="sm" onClick={save} disabled={saving || !budget}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save budget'}
        </Button>
      </div>
      <p className="mt-1.5 text-xs text-ink-muted">
        Over the cap, or over one site&apos;s share of it, this provider is skipped until next month and the others serve. Leave both empty for no budget.
      </p>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {budget && budget.top_tenants.length > 0 && (
        <p className="mt-1.5 text-xs text-ink-muted">
          Top sites: {budget.top_tenants.slice(0, 5).map((t) => `${t.tenant_site_id} ${usd(t.usd)}`).join(' · ')}
        </p>
      )}
    </div>
  );
}
